import type { FC, ReactNode } from 'react'
import type { Certificate } from '@/types'

import Image from 'next/image'
import { useRef } from 'react'
import { gsap, useGSAP } from '@/lib/motion/gsap'
import { formatYear, pad } from '@/lib/format'
import { cn } from '@/lib/utils'
import { useReveal } from '@/hooks/use-reveal'
import { PillLink } from '@/components/generals/arrow-link'
import { SplitWords } from '@/components/generals/split-words'
import { Viewfinder } from '@/components/generals/viewfinder'
import { showcaseContent } from '@/constants/content'

const columnSettings = [
    { speed: -18, className: '' },
    { speed: 14, className: 'mt-[18vh]' },
    { speed: -26, className: 'mt-[6vh]' }
]

interface ShowcaseProps {
    certificates: Certificate[]
    total: number
}

/** Three drifting columns of certificates behind an oversized title. */
export const Showcase: FC<ShowcaseProps> = ({ certificates = [], total = 0 }): ReactNode => {
    const scope = useRef<HTMLElement>(null)

    useReveal(scope)

    useGSAP(() => {
        const root = scope.current
        if (!root) return

        const mm = gsap.matchMedia()

        mm.add('(min-width: 768px) and (prefers-reduced-motion: no-preference)', () => {
            root.querySelectorAll<HTMLElement>('[data-column]').forEach((column) => {
                const speed = Number(column.dataset.column) || 0

                gsap.fromTo(column, { yPercent: speed * -0.5 }, {
                    yPercent: speed,
                    ease: 'none',
                    scrollTrigger: { trigger: root, start: 'top bottom', end: 'bottom top', scrub: true }
                })
            })

            gsap.fromTo(root.querySelector('[data-showcase-title]'), { scale: 0.86 }, {
                scale: 1.08,
                ease: 'none',
                scrollTrigger: { trigger: root, start: 'top bottom', end: 'bottom top', scrub: true }
            })
        })

        return () => mm.revert()
    }, { scope, dependencies: [certificates.length] })

    if (certificates.length === 0) return null

    const columns = columnSettings.map((settings, columnIndex) => ({
        ...settings,
        items: certificates
            .map((certificate, index) => ({ certificate, number: index + 1 }))
            .filter((_, index) => index % columnSettings.length === columnIndex)
    }))

    return (
        <section ref={scope} id='certificates-showcase' aria-labelledby='showcase-title' className='relative overflow-hidden bg-ink py-24 text-bone md:py-32'>
            <div className='gutter relative grid grid-cols-2 gap-3 md:grid-cols-3 md:gap-6'>
                {columns.map((column, columnIndex) => (
                    <div
                        key={columnIndex}
                        className={cn('flex flex-col gap-3 md:gap-6', column.className, columnIndex === 2 && 'hidden md:flex')}
                        data-column={column.speed}
                    >
                        {column.items.map(({ certificate, number }) => (
                            <figure key={certificate.id} className='group flex flex-col gap-2'>
                                <div className='relative aspect-[7/5] overflow-hidden bg-ink-raised'>
                                    {certificate.image && (
                                        <Image
                                            src={certificate.image}
                                            alt={certificate.image_alt ?? ''}
                                            fill
                                            sizes='(min-width: 768px) 32vw, 46vw'
                                            className='object-cover transition-transform duration-[1.6s] ease-[cubic-bezier(0.19,1,0.22,1)] group-hover:scale-105'
                                        />
                                    )}
                                    <Viewfinder size='sm' className='opacity-0 transition-opacity duration-500 group-hover:opacity-100' />
                                </div>

                                <figcaption className='eyebrow flex justify-between text-[0.6rem] text-stone'>
                                    <span>No. {pad(number, 3)}</span>
                                    <span>{formatYear(certificate.event_date)}</span>
                                </figcaption>
                            </figure>
                        ))}
                    </div>
                ))}

                <div className='pointer-events-none absolute inset-0 flex items-center justify-center'>
                    <h2
                        id='showcase-title'
                        className='text-center text-white mix-blend-difference'
                        data-showcase-title=''
                    >
                        <span className='eyebrow mb-6 block text-white/80'>
                            {showcaseContent.eyebrow} / {pad(total)}
                        </span>

                        <span className='block font-wide text-[clamp(3rem,12vw,14rem)] font-semibold uppercase leading-[0.82] tracking-[-0.06em]' data-reveal='words'>
                            <SplitWords text={showcaseContent.title} />
                        </span>

                        <span className='block font-serif text-[clamp(3rem,12vw,14rem)] italic leading-[0.82] tracking-[-0.03em]' data-reveal='words'>
                            <SplitWords text={showcaseContent.accent} />
                        </span>
                    </h2>
                </div>
            </div>

            <div className='gutter relative mt-16 flex justify-center md:mt-24'>
                <PillLink href='/certificates'>
                    {showcaseContent.action}
                </PillLink>
            </div>
        </section>
    )
}
